/**
 * /erp/inventory — current stock levels.
 *
 * Two filter dimensions:
 *   * by warehouse (a select that shows one warehouse's full inventory)
 *   * by item (search by code/name across all warehouses)
 *
 * The two filters are independent — leaving both empty shows the
 * full grid (default: zero rows hidden, matching the PRD intent of
 * "show me what I actually have"). Toggling "Show empty rows" re-runs
 * the query with include_zero=true so per-warehouse views can show
 * 'this product has 0 here' for forecasting / dispatch planning.
 *
 * Manager has access to a "Quick adjust" action on each row that
 * jumps to a small modal at the moment, and writes the delta as an
 * adjustment row. Sales is never on this page in v1 (the ERP shell
 * blocks them at /erp), but the action is still gated by user role
 * for defence in depth.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AlertCircle, Boxes, FileText, Loader2, Pencil, Plus, Search, Upload, X } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";

import {
  ApiError,
  type ErpInventoryDocumentLineParsed,
  type ErpInventoryDocumentParsed,
  type ErpInventoryDocumentDirection,
  type ErpInventoryDocumentType,
  type ErpInventoryAdjustmentCreate,
  type ErpInventoryRow,
  erpApi,
  isErpCatalogConflict,
  isInventoryNegativeBlocked,
  parseInventoryDocument,
} from "../lib/api";
import { useAuthStore } from "../lib/auth";
import { useInventory, useProducts, useWarehouses } from "../lib/erp";
import { toast } from "../lib/toast";
import { formatQty } from "../lib/units";

export const Route = createFileRoute("/_app/erp/inventory/")({
  component: InventoryStockLevelsPage,
});

interface RowKey {
  kind: "product" | "raw_material";
  itemId: string;
  warehouseId: string;
}

function InventoryStockLevelsPage() {
  const [warehouseId, setWarehouseId] = useState<string>("");
  const [includeZero, setIncludeZero] = useState(false);
  const [search, setSearch] = useState("");
  const [adjustingRow, setAdjustingRow] = useState<ErpInventoryRow | null>(null);
  const [documentAdjustOpen, setDocumentAdjustOpen] = useState(false);

  const warehousesQ = useWarehouses();
  const productsQ = useProducts();
  const inventoryQ = useInventory(
    warehouseId
      ? { warehouse_id: warehouseId, include_zero: includeZero }
      : { include_zero: includeZero },
  );

  const rows = inventoryQ.data?.items ?? [];
  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const needle = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        r.item_code.toLowerCase().includes(needle) ||
        r.item_name.toLowerCase().includes(needle) ||
        r.warehouse_code.toLowerCase().includes(needle) ||
        r.warehouse_name.toLowerCase().includes(needle),
    );
  }, [rows, search]);

  const user = useAuthStore((s) => s.user);
  const isProduction = user?.role === "production";
  const canAdjust = user?.role === "admin" || user?.role === "manager" || isProduction;

  return (
    <div className="space-y-4">
      <FilterBar
        warehouseId={warehouseId}
        onWarehouseChange={setWarehouseId}
        warehouses={warehousesQ.data?.items ?? []}
        search={search}
        onSearchChange={setSearch}
        includeZero={includeZero}
        onIncludeZeroChange={setIncludeZero}
        canAdjust={canAdjust}
        onDocumentAdjust={() => setDocumentAdjustOpen(true)}
      />

      {inventoryQ.isLoading ? (
        <p className="flex items-center gap-2 text-sm opacity-60">
          <Loader2 className="size-4 animate-spin" aria-hidden /> Loading inventory…
        </p>
      ) : inventoryQ.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">
          Could not load inventory: {(inventoryQ.error as Error).message}
        </p>
      ) : filteredRows.length === 0 ? (
        <EmptyState search={search} hasFilter={!!warehouseId} includeZero={includeZero} />
      ) : (
        <InventoryTable
          rows={filteredRows}
          canAdjust={canAdjust}
          onAdjust={(r) => setAdjustingRow(r)}
        />
      )}

      {adjustingRow && (
        <AdjustModal
          row={adjustingRow}
          isProduction={isProduction}
          onClose={() => setAdjustingRow(null)}
        />
      )}
      {documentAdjustOpen && (
        <DocumentAdjustModal
          initialWarehouseId={warehouseId}
          warehouses={warehousesQ.data?.items ?? []}
          products={productsQ.data?.items ?? []}
          onClose={() => setDocumentAdjustOpen(false)}
        />
      )}
    </div>
  );
}

function FilterBar({
  warehouseId,
  onWarehouseChange,
  warehouses,
  search,
  onSearchChange,
  includeZero,
  onIncludeZeroChange,
  canAdjust,
  onDocumentAdjust,
}: {
  warehouseId: string;
  onWarehouseChange: (id: string) => void;
  warehouses: { id: string; code: string; name: string; is_active: boolean }[];
  search: string;
  onSearchChange: (s: string) => void;
  includeZero: boolean;
  onIncludeZeroChange: (b: boolean) => void;
  canAdjust: boolean;
  onDocumentAdjust: () => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-xl border border-[var(--border)] p-3">
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium opacity-80">Warehouse</span>
        <select
          value={warehouseId}
          onChange={(e) => onWarehouseChange(e.target.value)}
          className="rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
        >
          <option value="">All warehouses</option>
          {warehouses.map((w) => (
            <option key={w.id} value={w.id}>
              {w.code} — {w.name}
              {!w.is_active ? " (inactive)" : ""}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-1 flex-col gap-1">
        <span className="text-xs font-medium opacity-80">Search</span>
        <div className="flex items-center gap-1.5 rounded-md border border-[var(--border)] px-2 py-1.5 focus-within:border-[var(--accent)]">
          <Search className="size-4 opacity-50" aria-hidden />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Item or warehouse code/name"
            className="w-full bg-transparent text-sm outline-none"
          />
        </div>
      </label>

      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={includeZero}
          onChange={(e) => onIncludeZeroChange(e.target.checked)}
          className="h-4 w-4 cursor-pointer rounded border border-[var(--border)]"
        />
        <span>Show empty (qty = 0) rows</span>
      </label>

      {canAdjust && (
        <button
          type="button"
          onClick={onDocumentAdjust}
          className="inline-flex min-h-10 items-center gap-2 rounded-md bg-[var(--accent)] px-3 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          <FileText className="size-4" aria-hidden />
          Adjust stock from document
        </button>
      )}
    </div>
  );
}

function DocumentAdjustModal({
  initialWarehouseId,
  warehouses,
  products,
  onClose,
}: {
  initialWarehouseId: string;
  warehouses: { id: string; code: string; name: string; is_active: boolean }[];
  products: { id: string; code: string; name: string; uom: string; is_active: boolean }[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [documentType, setDocumentType] =
    useState<ErpInventoryDocumentType>("delivery_challan");
  const [stockDirection, setStockDirection] = useState<ErpInventoryDocumentDirection>("add");
  const [sourceWarehouseId, setSourceWarehouseId] = useState(initialWarehouseId);
  const [destinationWarehouseId, setDestinationWarehouseId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [parsed, setParsed] = useState<ErpInventoryDocumentParsed | null>(null);
  const [lines, setLines] = useState<ErpInventoryDocumentLineParsed[]>([]);
  const [error, setError] = useState<string | null>(null);

  const selectFile = (nextFile: File | null) => {
    if (!nextFile) return;
    const isPdf = nextFile.type === "application/pdf" || nextFile.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      setError("Choose a PDF challan or invoice.");
      return;
    }
    if (nextFile.size > 10 * 1024 * 1024) {
      setError("The PDF is larger than the 10 MB limit.");
      return;
    }
    setFile(nextFile);
    setParsed(null);
    setLines([]);
    setError(null);
  };

  const removeFile = () => {
    setFile(null);
    setParsed(null);
    setLines([]);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const parseMutation = useMutation({
    mutationFn: () => {
      if (!sourceWarehouseId) throw new Error("Select a warehouse first.");
      if (!file) throw new Error("Choose a PDF first.");
      if (stockDirection === "transfer" && !destinationWarehouseId) {
        throw new Error("Select a destination warehouse.");
      }
      if (stockDirection === "transfer" && sourceWarehouseId === destinationWarehouseId) {
        throw new Error("The source and destination warehouses must be different.");
      }
      return parseInventoryDocument(
        sourceWarehouseId,
        documentType,
        stockDirection,
        file,
        stockDirection === "transfer" ? destinationWarehouseId : undefined,
      );
    },
    onSuccess: (result) => {
      setParsed(result);
      setLines(result.lines);
      setError(null);
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Could not read the PDF."),
  });

  const applyMutation = useMutation({
    mutationFn: () => {
      if (!parsed) throw new Error("Upload and review a PDF first.");
      const invalid = lines.find((line) => !line.product_id || Number(line.quantity) <= 0);
      if (invalid) throw new Error("Match every line to a product and enter a positive quantity.");
      return erpApi.inventory.applyDocument({
        parse_token: parsed.parse_token,
        lines: lines.map((line) => ({
          source_sku: line.source_sku,
          product_id: line.product_id as string,
          description: line.description,
          quantity: String(line.quantity),
          uom: line.uom,
        })),
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["erp", "inventory"] });
      toast.success(
        `${result.document_number} applied: ${result.lines.length} stock item${result.lines.length === 1 ? "" : "s"} updated.`,
      );
      onClose();
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : "Could not apply this document.";
      setError(message);
      toast.error(message);
    },
  });

  const direction = parsed?.direction ?? stockDirection;
  const sourceWarehouse = warehouses.find((warehouse) => warehouse.id === sourceWarehouseId);
  const destinationWarehouse = warehouses.find(
    (warehouse) => warehouse.id === destinationWarehouseId,
  );
  const activeWarehouses = warehouses.filter((warehouse) => warehouse.is_active);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="document-adjust-title"
    >
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 cursor-default bg-black/50"
        onClick={onClose}
      />
      <section className="relative max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-xl border border-[var(--border)] bg-[var(--bg)] p-4 shadow-xl sm:p-6">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h2 id="document-adjust-title" className="text-lg font-semibold tracking-tight">
              Adjust stock from document
            </h2>
            <p className="mt-1 text-sm opacity-65">Choose the movement, warehouse route, and supporting PDF.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-2 hover:bg-black/5 dark:hover:bg-white/5">
            <X className="size-5" aria-hidden />
          </button>
        </header>

        {!parsed ? (
          <div className="mt-5 space-y-5">
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Stock movement</legend>
              <div className="grid gap-3 sm:grid-cols-3">
                {([
                  ["add", "Stock in", "Add received items to this warehouse"],
                  ["subtract", "Stock out", "Subtract items leaving this warehouse"],
                  ["transfer", "Transfer", "Move stock from one warehouse to another"],
                ] as const).map(([value, label, help]) => (
                  <label key={value} className={`cursor-pointer rounded-xl border p-4 ${stockDirection === value ? "border-[var(--accent)] bg-blue-500/5" : "border-[var(--border)]"}`}>
                    <span className="flex items-center gap-2 font-medium">
                      <input
                        type="radio"
                        name="stockDirection"
                        value={value}
                        checked={stockDirection === value}
                        onChange={() => {
                          setStockDirection(value);
                          if (value === "transfer") setDocumentType("delivery_challan");
                          if (value === "subtract" && documentType === "sales_return") {
                            setDocumentType("delivery_challan");
                          }
                          if (value === "add" && documentType === "invoice") {
                            setDocumentType("delivery_challan");
                          }
                        }}
                      />
                      {label}
                    </span>
                    <span className="mt-1 block pl-6 text-xs opacity-65">{help}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">
                {stockDirection === "transfer"
                  ? "Warehouse route"
                  : stockDirection === "add"
                    ? "Receiving warehouse"
                    : "Source warehouse"}
              </legend>
              <div className={`grid gap-3 ${stockDirection === "transfer" ? "sm:grid-cols-2" : "sm:max-w-xl"}`}>
                <label className="space-y-1">
                  <span className="text-xs font-medium">
                    {stockDirection === "transfer" ? "From warehouse" : "Warehouse"}
                  </span>
                  <select
                    value={sourceWarehouseId}
                    onChange={(event) => {
                      setSourceWarehouseId(event.target.value);
                      setParsed(null);
                      setLines([]);
                      setError(null);
                    }}
                    className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm"
                  >
                    <option value="">Select warehouse</option>
                    {activeWarehouses.map((warehouse) => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.code} — {warehouse.name}
                      </option>
                    ))}
                  </select>
                </label>
                {stockDirection === "transfer" && (
                  <label className="space-y-1">
                    <span className="text-xs font-medium">To warehouse</span>
                    <select
                      value={destinationWarehouseId}
                      onChange={(event) => {
                        setDestinationWarehouseId(event.target.value);
                        setParsed(null);
                        setLines([]);
                        setError(null);
                      }}
                      className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm"
                    >
                      <option value="">Select destination</option>
                      {activeWarehouses
                        .filter((warehouse) => warehouse.id !== sourceWarehouseId)
                        .map((warehouse) => (
                          <option key={warehouse.id} value={warehouse.id}>
                            {warehouse.code} — {warehouse.name}
                          </option>
                        ))}
                    </select>
                  </label>
                )}
              </div>
            </fieldset>

            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Document type</legend>
              <div className="grid gap-3 sm:grid-cols-3">
                {([
                  ["delivery_challan", "Delivery challan", stockDirection === "add" ? "Stock received on a challan" : stockDirection === "transfer" ? "Warehouse-to-warehouse transfer challan" : "Stock sent out on a challan"],
                  ["invoice", "Invoice", stockDirection === "subtract" ? "Stock sold or dispatched on an invoice" : "Invoices are available only for stock-out"],
                  ["sales_return", "Sales return / Credit note", stockDirection === "add" ? "Returned items received back into this warehouse" : "Sales returns are available only for stock-in"],
                ] as const).map(([value, label, help]) => (
                  <label key={value} className={`rounded-xl border p-4 ${(value === "invoice" && stockDirection !== "subtract") || (value === "sales_return" && stockDirection !== "add") || (stockDirection === "transfer" && value !== "delivery_challan") ? "cursor-not-allowed opacity-45" : "cursor-pointer"} ${documentType === value ? "border-[var(--accent)] bg-blue-500/5" : "border-[var(--border)]"}`}>
                    <span className="flex items-center gap-2 font-medium">
                      <input type="radio" name="documentType" value={value} checked={documentType === value} disabled={(value === "invoice" && stockDirection !== "subtract") || (value === "sales_return" && stockDirection !== "add") || (stockDirection === "transfer" && value !== "delivery_challan")} onChange={() => setDocumentType(value)} />
                      {label}
                    </span>
                    <span className="mt-1 block pl-6 text-xs opacity-65">{help}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <label
              onDragEnter={(event) => {
                event.preventDefault();
                setIsDraggingFile(true);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                event.dataTransfer.dropEffect = "copy";
                setIsDraggingFile(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  setIsDraggingFile(false);
                }
              }}
              onDrop={(event) => {
                event.preventDefault();
                setIsDraggingFile(false);
                selectFile(event.dataTransfer.files?.[0] ?? null);
              }}
              className={`block cursor-pointer rounded-xl border-2 border-dashed p-7 text-center transition-colors ${
                isDraggingFile
                  ? "border-[var(--accent)] bg-blue-500/10"
                  : "border-[var(--border)] bg-[var(--surface)] hover:border-[var(--accent)] hover:bg-blue-500/5"
              }`}
            >
              <span className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-blue-500/10 text-[var(--accent)]">
                <Upload className="size-6" aria-hidden />
              </span>
              <span className="block text-base font-semibold">
                {isDraggingFile ? "Drop the PDF here" : "Drag and drop your PDF here"}
              </span>
              <span className="mt-1 block text-sm opacity-60">or</span>
              <span className="mx-auto mt-3 inline-flex min-h-10 items-center rounded-md bg-[var(--accent)] px-4 text-sm font-medium text-white">
                Browse PDF
              </span>
              <span className="mt-3 block text-xs opacity-60">
                Delivery challan, invoice, or sales return/credit note · PDF only · Maximum 10 MB
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="sr-only"
                onChange={(event) => selectFile(event.target.files?.[0] ?? null)}
              />
            </label>
            {file && (
              <div className="flex items-center gap-3 rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4">
                <span className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
                  <FileText className="size-6" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold" title={file.name}>{file.name}</p>
                  <p className="mt-0.5 text-xs opacity-65">PDF · {formatFileSize(file.size)} · Ready to extract</p>
                </div>
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    removeFile();
                  }}
                  className="inline-flex min-h-10 shrink-0 items-center gap-1 rounded-md border border-[var(--border)] px-3 text-xs font-medium hover:bg-black/5 dark:hover:bg-white/5"
                >
                  <X className="size-3.5" aria-hidden /> Remove
                </button>
              </div>
            )}
            {error && <p className="text-sm text-red-700 dark:text-red-300">{error}</p>}
            <div className="flex justify-end">
              <button
                type="button"
                disabled={
                  !file ||
                  !sourceWarehouseId ||
                  (stockDirection === "transfer" && !destinationWarehouseId) ||
                  parseMutation.isPending
                }
                onClick={() => parseMutation.mutate()}
                className="inline-flex items-center gap-2 rounded-md bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {parseMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <FileText className="size-4" />}
                Extract document
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--border)] p-3 text-sm">
              <div>
                <span className="opacity-60">Document</span><br />
                <span className="font-medium">{parsed.document_number}</span> · {parsed.document_date ?? "No date"}
                {parsed.original_invoice_number && (
                  <p className="mt-1 text-xs opacity-70">
                    Original invoice: {parsed.original_invoice_number}
                  </p>
                )}
                <p className="mt-1 text-xs opacity-70">
                  {direction === "transfer"
                    ? `${sourceWarehouse?.code ?? "Source"} → ${destinationWarehouse?.code ?? "Destination"}`
                    : `${sourceWarehouse?.code ?? "Warehouse"} — ${sourceWarehouse?.name ?? ""}`}
                </p>
              </div>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${direction === "add" ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : direction === "transfer" ? "bg-blue-500/10 text-blue-700 dark:text-blue-300" : "bg-amber-500/10 text-amber-700 dark:text-amber-300"}`}>
                {direction === "add" ? "ADD STOCK" : direction === "transfer" ? "TRANSFER STOCK" : "SUBTRACT STOCK"}
              </span>
            </div>

            <div className="overflow-x-auto rounded-xl border border-[var(--border)]">
              <table className="w-full min-w-[900px] text-sm">
                <thead className="border-b border-[var(--border)] bg-black/[0.02] text-left text-xs uppercase tracking-wide opacity-70 dark:bg-white/[0.02]">
                  <tr>
                    <th className="px-3 py-2">Document SKU</th><th className="px-3 py-2">Product match</th><th className="px-3 py-2">Description</th><th className="px-3 py-2">Qty</th><th className="px-3 py-2">UOM</th>
                    {direction === "transfer" ? (
                      <><th className="px-3 py-2 text-right">From current</th><th className="px-3 py-2 text-right">From after</th><th className="px-3 py-2 text-right">To current</th><th className="px-3 py-2 text-right">To after</th></>
                    ) : (
                      <><th className="px-3 py-2 text-right">Current</th><th className="px-3 py-2 text-right">After</th></>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, index) => {
                    const current = line.current_qty === null ? null : Number(line.current_qty);
                    const resulting = current === null ? null : current + (direction === "add" ? Number(line.quantity) : -Number(line.quantity));
                    const destinationCurrent = line.destination_current_qty === null ? null : Number(line.destination_current_qty);
                    const destinationResulting = destinationCurrent === null ? null : destinationCurrent + Number(line.quantity);
                    return (
                      <tr key={`${line.source_sku}-${index}`} className="border-b border-[var(--border)] last:border-0">
                        <td className="px-3 py-3 font-mono text-xs">{line.source_sku}</td>
                        <td className="px-3 py-3">
                          <select
                            value={line.product_id ?? ""}
                            onChange={(event) => setLines((currentLines) => currentLines.map((item, itemIndex) => itemIndex === index ? { ...item, product_id: event.target.value || null, match_type: event.target.value ? item.match_type : "unmatched", current_qty: event.target.value === line.product_id ? item.current_qty : null, resulting_qty: null, destination_current_qty: event.target.value === line.product_id ? item.destination_current_qty : null, destination_resulting_qty: null } : item))}
                            className="w-full min-w-52 rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5"
                          >
                            <option value="">— Select product —</option>
                            {products.filter((product) => product.is_active).map((product) => <option key={product.id} value={product.id}>{product.code} — {product.name}</option>)}
                          </select>
                          <span className={`mt-1 block text-[11px] ${line.match_type === "unmatched" ? "text-red-600" : "text-emerald-600"}`}>{line.match_type.replace("_", " ")}</span>
                        </td>
                        <td className="px-3 py-3 text-xs">{line.description}</td>
                        <td className="px-3 py-3"><input type="number" min="0.001" step="0.001" value={line.quantity} onChange={(event) => setLines((currentLines) => currentLines.map((item, itemIndex) => itemIndex === index ? { ...item, quantity: event.target.value } : item))} className="w-24 rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 font-mono" /></td>
                        <td className="px-3 py-3">{line.uom}</td>
                        <td className="px-3 py-3 text-right font-mono">{current === null ? "—" : current}</td>
                        <td className={`px-3 py-3 text-right font-mono ${resulting !== null && resulting < 0 ? "text-red-600" : ""}`}>{resulting === null ? "On submit" : resulting}</td>
                        {direction === "transfer" && (
                          <>
                            <td className="px-3 py-3 text-right font-mono">{destinationCurrent === null ? "—" : destinationCurrent}</td>
                            <td className="px-3 py-3 text-right font-mono">{destinationResulting === null ? "On submit" : destinationResulting}</td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs opacity-65">Verify every product and quantity. Submission is atomic: for transfers, the source subtraction and destination addition either both succeed or neither is changed.</p>
            {error && <p className="text-sm text-red-700 dark:text-red-300">{error}</p>}
            <div className="flex flex-wrap justify-between gap-3">
              <button type="button" onClick={() => { setParsed(null); setLines([]); setError(null); }} className="rounded-md border border-[var(--border)] px-3 py-2 text-sm">Choose another PDF</button>
              <button type="button" disabled={applyMutation.isPending} onClick={() => applyMutation.mutate()} className="inline-flex items-center gap-2 rounded-md bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
                {applyMutation.isPending && <Loader2 className="size-4 animate-spin" />}
                Verify and update stock
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function EmptyState({
  search,
  hasFilter,
  includeZero,
}: {
  search: string;
  hasFilter: boolean;
  includeZero: boolean;
}) {
  return (
    <div className="rounded-xl border border-dashed border-[var(--border)] p-10 text-center">
      <Boxes className="mx-auto mb-3 size-8 opacity-40" aria-hidden />
      {hasFilter || search ? (
        <>
          <p className="text-sm font-medium">No matching inventory.</p>
          <p className="mt-1 text-sm opacity-60">Clear the filters or try a different search.</p>
        </>
      ) : (
        <>
          <p className="text-sm font-medium">No inventory yet.</p>
          <p className="mt-1 text-sm opacity-60">
            Adjustments and manufacturing jobs populate this view as they happen.
            {!includeZero && " Toggle 'Show empty rows' to see items with qty = 0."}
          </p>
        </>
      )}
    </div>
  );
}

function InventoryTable({
  rows,
  canAdjust,
  onAdjust,
}: {
  rows: ErpInventoryRow[];
  canAdjust: boolean;
  onAdjust: (r: ErpInventoryRow) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-xl border border-[var(--border)]">
      <table className="w-full text-sm">
        <thead className="border-b border-[var(--border)] bg-black/[0.02] text-left text-xs uppercase tracking-wider opacity-70 dark:bg-white/[0.02]">
          <tr>
            <th className="px-3 py-2">Warehouse</th>
            <th className="px-3 py-2">Item</th>
            <th className="px-3 py-2">UoM</th>
            <th className="px-3 py-2 text-right">Qty</th>
            {canAdjust && <th className="px-3 py-2" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const key: RowKey = {
              kind: row.item_kind,
              itemId: row.item_id,
              warehouseId: row.warehouse_id,
            };
            const qtyValue = Number(row.qty);
            const isNegative = qtyValue < 0;
            return (
              <tr
                key={`${key.kind}|${key.itemId}|${key.warehouseId}`}
                className={`border-b border-[var(--border)] last:border-0 ${isNegative ? "bg-red-50/40 dark:bg-red-950/10" : ""}`}
              >
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="rounded-md bg-black/5 px-1.5 py-0.5 font-mono text-[11px] dark:bg-white/10">
                      {row.warehouse_code}
                    </span>
                    <span>{row.warehouse_name}</span>
                    {!row.warehouse_active && (
                      <span className="rounded-full bg-black/10 px-2 py-0.5 text-[10px] uppercase tracking-wider opacity-60 dark:bg-white/10">
                        Inactive
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="rounded-md bg-black/5 px-1.5 py-0.5 font-mono text-[11px] dark:bg-white/10">
                      {row.item_code}
                    </span>
                    <span>{row.item_name}</span>
                    <span className="rounded-full border border-[var(--border)] px-2 py-0.5 text-[10px] uppercase tracking-wider opacity-70">
                      {row.item_kind === "product" ? "Product" : "Raw"}
                    </span>
                    {!row.item_active && (
                      <span className="rounded-full bg-black/10 px-2 py-0.5 text-[10px] uppercase tracking-wider opacity-60 dark:bg-white/10">
                        Inactive
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 opacity-70">{row.item_uom}</td>
                <td
                  className={`px-3 py-2 text-right font-mono ${isNegative ? "text-red-700 dark:text-red-300" : ""}`}
                >
                  {formatQty(row.qty, row.item_uom)}
                </td>
                {canAdjust && (
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => onAdjust(row)}
                      className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-black/5 dark:hover:bg-white/5"
                    >
                      <Pencil className="size-3" aria-hidden />
                      Adjust
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AdjustModal({
  row,
  isProduction,
  onClose,
}: {
  row: ErpInventoryRow;
  isProduction: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [allowNegative, setAllowNegative] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const mutation = useMutation({
    mutationFn: (body: ErpInventoryAdjustmentCreate) => erpApi.inventory.createAdjustment(body),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["erp", "inventory"] });
      const wMsg = data.warned ? " (qty went negative)" : "";
      toast.success(`Stock now ${data.qty_after}${wMsg}`);
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        if (isInventoryNegativeBlocked(err.detail)) {
          setError(
            isProduction
              ? `This adjustment would make stock negative (current qty ${err.detail.current_qty}).`
              : `Already at qty ${err.detail.current_qty}. Tick 'Allow negative' to push further into the red.`,
          );
          return;
        }
        if (isErpCatalogConflict(err.detail)) {
          setError(err.detail.message);
          return;
        }
      }
      const msg = err instanceof ApiError ? err.message : "Could not save adjustment.";
      setError(msg);
      toast.error(msg);
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const trimmed = delta.trim();
    if (!trimmed || trimmed === "0") {
      setError("Enter a non-zero delta.");
      return;
    }
    if (!reason.trim()) {
      setError("Reason is required.");
      return;
    }
    const body: ErpInventoryAdjustmentCreate = {
      item_kind: row.item_kind,
      warehouse_id: row.warehouse_id,
      delta: trimmed,
      reason: reason.trim(),
      allow_negative: isProduction ? false : allowNegative,
    };
    if (row.item_kind === "product") body.product_id = row.item_id;
    else body.raw_material_id = row.item_id;
    mutation.mutate(body);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      // biome-ignore lint/a11y/useSemanticElements: HTML <dialog> doesn't compose with our backdrop click-out pattern; div+role="dialog" is the existing project convention.
      role="dialog"
      aria-modal="true"
      aria-labelledby="adjust-stock-title"
    >
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 cursor-default bg-black/40"
        onClick={onClose}
      />
      <form
        onSubmit={onSubmit}
        className="relative w-full max-w-md space-y-4 rounded-xl border border-[var(--border)] bg-[var(--bg)] p-5 shadow-lg"
      >
        <header className="space-y-1">
          <h2 id="adjust-stock-title" className="text-lg font-semibold tracking-tight">
            Adjust stock
          </h2>
          <p className="text-sm opacity-60">
            <span className="rounded-md bg-black/5 px-1.5 py-0.5 font-mono text-[11px] dark:bg-white/10">
              {row.item_code}
            </span>{" "}
            <span className="font-medium">{row.item_name}</span>
            {" at "}
            <span className="rounded-md bg-black/5 px-1.5 py-0.5 font-mono text-[11px] dark:bg-white/10">
              {row.warehouse_code}
            </span>{" "}
            <span>{row.warehouse_name}</span>
          </p>
          <p className="text-xs opacity-60">
            Current qty: <span className="font-mono">{formatQty(row.qty, row.item_uom)}</span>{" "}
            {row.item_uom}
          </p>
        </header>

        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">
            Delta <span className="text-red-500">*</span>
          </span>
          <input
            type="text"
            value={delta}
            onChange={(e) => setDelta(e.target.value)}
            placeholder="e.g. +5 or -2.5"
            inputMode="decimal"
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 font-mono text-sm outline-none focus:border-[var(--accent)]"
          />
          <span className="block text-[11px] opacity-60">
            Positive numbers add stock, negative numbers remove it.
          </span>
        </label>

        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">
            Reason <span className="text-red-500">*</span>
          </span>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            placeholder="Why is this needed? (audit log entry)"
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
          />
        </label>

        {!isProduction && Number(row.qty) < 0 && (
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={allowNegative}
              onChange={(e) => setAllowNegative(e.target.checked)}
              className="h-4 w-4 cursor-pointer rounded border border-[var(--border)]"
            />
            <span>Allow further-negative (manager override)</span>
          </label>
        )}

        {error && (
          <p className="flex items-center gap-1 text-xs text-red-700 dark:text-red-300">
            <AlertCircle className="size-3" aria-hidden />
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
          >
            <X className="size-4" aria-hidden />
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending || !delta.trim() || !reason.trim()}
            className="inline-flex items-center gap-1 rounded-md bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {mutation.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Plus className="size-4" aria-hidden />
            )}
            Save adjustment
          </button>
        </div>
      </form>
    </div>
  );
}
